import React from "react";
import { Marker, FlyToInterpolator } from "react-map-gl";
import { scaleLinear, scaleLog } from "d3-scale";

import useSupercluster from "use-supercluster";

import { addThousandSeparator } from "../../util/parseTableData";
import MarkersStyle from "./markers.module.css";

const Markers = ({
  data,
  mapRef,
  viewport,
  setViewport,
  pulseCountries,
  minCount,
  maxCount,
  setShowPopup,
  setLatFocus,
  setLngFocus,
  setOffsetFocus,
  setTooltip,
}) => {
  const maxRadius = 8;
  const minRadius = 7;
  // const radius = 15;

  const points = React.useMemo(() => {
    return data
      .filter((item) => {
        return !isNaN(parseFloat(item.lat)) && !isNaN(parseFloat(item.lng));
      })
      .map((data) => {
        let recency = pulseCountries.findIndex((element) => {
          return element === data.city;
        });
        if (recency === -1) {
          recency = pulseCountries.length + 1;
        }
        return {
          type: "Feature",
          properties: {
            cluster: false,
            city: data.city,
            count: data.count,
            country: data.country,
            countryCode: data.countryCode,
            recency: recency,
          },
          geometry: {
            type: "Point",
            coordinates: [parseFloat(data.lng), parseFloat(data.lat)],
          },
        };
      });
  }, [data, pulseCountries]);

  const bounds = mapRef.current
    ? mapRef.current.getMap().getBounds().toArray().flat()
    : null;
  const { clusters, supercluster } = useSupercluster({
    points: points,
    bounds: bounds,
    zoom: viewport.zoom,
    options: {
      radius: 7,
      maxZoom: 4,
      map: (props) => {
        return { count: props.count, recency: props.recency };
      },
      reduce: (accumulated, props) => {
        accumulated.count += props.count;
        accumulated.recency = Math.min(
          props.recency,
          accumulated.recency ?? 999
        );
      },
    },
  });

  // if (data.length >= 100) {
    const clusterMarkers = clusters.map((cluster) => {
      const [long, lat] = cluster.geometry.coordinates;
      const { cluster: isCluster, count, recency } = cluster.properties;
      let radius = scaleLog()
        .domain([minCount, maxCount])
        .range([minRadius, maxRadius])(count);
      if (isNaN(radius)) {
        radius = 0;
      }

      if (isCluster) {
        return (
          <Marker
            key={cluster.id}
            offsetTop={-radius}
            offsetLeft={-radius / 2}
            latitude={lat}
            longitude={long}
          >
            <div
              className={`${MarkersStyle.marker} ${MarkersStyle.clusterMarker}`}
              style={{
                background: scaleLinear()
                  .clamp(true)
                  .domain([1, pulseCountries.length])
                  .range(["#ff3c19", "#4d302b"])(recency),
                height: radius,
                width: radius,
              }}
              onMouseEnter={(e) => {
                setLatFocus(lat);
                setLngFocus(long);
                setOffsetFocus(-(radius / 2));
                setShowPopup(true);
                setTooltip(
                  `${addThousandSeparator(
                    count.toString()
                  )} sequence${count>1?"s":""} recorded in total`
                );
              }}
              onMouseLeave={() => {
                setShowPopup(false);
              }}
              onClick={() => {
                const expansionZoom = Math.min(
                  supercluster.getClusterExpansionZoom(cluster.id, 4)
                );
                setViewport({
                  ...viewport,
                  latitude: lat,
                  longitude: long,
                  zoom: expansionZoom,
                  transitionInterpolator: new FlyToInterpolator({
                    speed: 2,
                  }),
                  transitionDuration: "auto",
                });
              }}
            />
          </Marker>
        );
      } else {
        const { city, count, country } = cluster.properties;
        return (
          <Marker
            key={city}
            offsetTop={-radius}
            offsetLeft={-radius / 2}
            latitude={lat}
            longitude={long}
          >
            <div
              className={`${MarkersStyle.marker}`}
              style={{
                background: scaleLinear()
                  .clamp(true)
                  .domain([1, pulseCountries.length])
                  .range(["#ff3c19", "#4d302b"])(recency),
                height: radius,
                width: radius,
              }}
              onMouseEnter={(e) => {
                setLatFocus(lat);
                setLngFocus(long);
                setOffsetFocus(-(radius / 2));
                setShowPopup(true);
                setTooltip(
                  `${city}, ${country}: ${addThousandSeparator(
                    count.toString()
                  )} sequence${count>1?"s":""}`
                );
              }}
              onMouseLeave={() => {
                setShowPopup(false);
              }}
            />
          </Marker>
        );
      }
    });

    return clusterMarkers;

  // } else {
  //   const markers = points.map(
  //     ({ properties, geometry }) => {
  //       // let radius = Math.max(
  //       //   minRadius,
  //       //   Math.ceil((count / maxCount) * maxRadius)
  //       // );
  //       // if (count==0) {
  //       //   radius = 0;
  //       // }
  //       let { cluster, city, count, country, countryCode, recency } = properties;
  //       let [lng, lat] = geometry.coordinates;

  //       return !isNaN(lat) && !isNaN(lng) ? (
  //         <Marker
  //           key={city}
  //           offsetTop={-radius}
  //           offsetLeft={-radius / 2}
  //           latitude={lat}
  //           longitude={lng}
  //         >
  //           <div
  //             className={`${MarkersStyle.marker}`}
  //             style={{
  //               background: scaleLinear()
  //                 .clamp(true)
  //                 .domain([1, pulseCountries.length])
  //                 .range(["#ff3c19", "#4d302b"])(recency),
  //               height: radius,
  //               width: radius,
  //             }}
  //             onMouseEnter={(e) => {
  //               setLatFocus(lat);
  //               setLngFocus(lng);
  //               setOffsetFocus(-(radius / 2));
  //               setShowPopup(true);
  //               setTooltip(`${city}, ${country}: ${addThousandSeparator(
  //                 count.toString()
  //               )} variant`);
  //             }}
  //             onMouseLeave={() => {
  //               setShowPopup(false);
  //             }}
  //           />
  //         </Marker>
  //       ) : null;
  //     }
  //   );

  //   return markers;
  // }
};

export default React.memo(Markers);
